import { create } from "zustand"
import {useRef, useEffect, Fragment } from 'react'
import { Modal } from 'bootstrap'
import { DetailFileDownload, DetailItem } from "../../../../components/layout"
import { ApplicationAttachment, IWebFileAttachment } from "../../../core/applications"
import { isPopulatedArray, showDate } from "../../../../components/helpers"
import { ApplicationFileDownload } from "../../../../components/widgets"


interface ViewDraftPopupState {
  show: boolean
  details: Record<string, any>
  toggleShow: (show: boolean) => void
  setDetails: (details: Record<string, any>) => void
  clear: () => void,
}

export const useViewDraftPopupState = create<ViewDraftPopupState>()((set) => ({
  show: false,
  details: {},
  toggleShow: (show: boolean) => set({show}),
  setDetails: (details: Record<string, any>) => set({details}),
  clear: () => set({
    show: false,
    details: {},
  }),
}));


export const ViewDraftPopup = () => {

  const { show, details, toggleShow } = useViewDraftPopupState(state => state);

  const modalRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (! (modalRef && modalRef.current)) return;

    const modalEle = modalRef.current;
    if (show) {
      const bsModal = new Modal(modalEle, {
      backdrop: false,
      keyboard: false
      })
      bsModal.show();
      return;
    }
    const bsModal= Modal.getInstance(modalEle)
    if (bsModal) bsModal.hide();

    // eslint-disable-next-line
  }, [show, modalRef]);

  return (
    <div className="modal" ref={modalRef} data-bs-backdrop="static" data-bs-keyboard="false" aria-labelledby="staticBackdropLabel" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered min-w-500px">
        <div className="modal-content w-auto mx-auto min-w-500px">
          <div className="modal-header">
            <h1 className="modal-title fs-5" id="staticBackdropLabel">View Draft Details</h1>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={() => toggleShow(false)}></button>
          </div>
          <div className="modal-body">
            <div className="d-flex flex-column gap-2">
              { details?.verdict  && 
              <DetailItem labelText="Verdict" content={`${details.verdict.toUpperCase()}`} />
              }
              { (details?.samplingFormId && details?.samplingFormIdAttachmentName) &&
              <DetailFileDownload labelText="Sampling Form" file={{ id: details.samplingFormId, fileName: details.samplingFormIdAttachmentName} as ApplicationAttachment } />        
              }
              { (details?.ncrAttachmentId > 0 && details?.ncrAttachmentIdAttachmentName) &&
              <DetailFileDownload labelText="Non-Conformity Report" file={{ id: details.ncrAttachmentId, fileName: details.ncrAttachmentIdAttachmentName} as ApplicationAttachment } />        
              }
              { (details?.majorNCFound) && 
              <DetailItem labelText="Major Non Conformity Found" >
                <div className='form-check form-check-sm form-check-custom form-check-solid'>
                  <input className='form-check-input pe-none' type='checkbox' checked={details?.majorNCFound ? true : false} readOnly />
                </div>
              </DetailItem>
              }    
              { details?.testDate  && 
              <DetailItem labelText="Date of Surveillance Test" content={`${details.testDate}`} />
              }
              { (details?.samplings && isPopulatedArray(details.samplings)) &&
              <>
              <div className="col-6 fs-8 fw-bold text-muted">SURVEILLANCE TEST RESULT</div>
              { details.samplings.map( (dat: any , idx: number) => (
              <DetailFileDownload 
                key={`sampling_${idx}`} 
                labelText={dat.testReportReference} 
                file={{ id: dat.surveillanceAttachmentId, fileName: dat.surveillanceAttachmentIdAttachmentName} as ApplicationAttachment } 
              />        
              ))
              }
              </>
              }
              { details?.inspectionDate  && 
              <DetailItem labelText="Inspection Date" content={showDate(details.inspectionDate)} />
              }
              { (details?.transports && isPopulatedArray(details.transports)) &&
              <>
              <div className="col-6 fs-8 fw-bold text-muted">TRANSPORT DETAILS</div>
              {details.transports.map((dat: any, idx: number) => (
              <Fragment key={`transport_${idx}`}>
              { dat?.type  && 
              <DetailItem labelText="Type" content={`${dat.type}`} />
              }
              { dat?.transportDetails  && 
              <DetailItem labelText="Transport Details" content={`${dat.transportDetails}`} />
              }
              { (dat?.transportAttachments && isPopulatedArray(dat.transportAttachments)) &&
              <DetailItem labelText="Transport Attachment/s">
                <div>
                { dat.transportAttachments.map( (fdat: IWebFileAttachment) => (
                <ApplicationFileDownload key={`trx${fdat.id}`} file={{id: fdat.id, fileName: fdat.name} as ApplicationAttachment} />
                ))}
                </div>
              </DetailItem>  
              }
              </Fragment>
              ))}
              </>
              }

              { details?.durationOption  && 
              <DetailItem labelText="Duration of Inspection" content={`${details.durationOption}`} />
              }
              {details?.durationRange  && 
              <DetailItem labelText="Duration Range" content={`${details.durationRange}`} />
              }
              { (isPopulatedArray(details?.durationAttachments as Array<IWebFileAttachment>)) &&
              <DetailItem labelText="Duration Attachment/s">
                <div>
                { details.durationAttachments.map( (dat: IWebFileAttachment) => (
                <ApplicationFileDownload key={`duration${dat.id}`} file={{id: dat.id, fileName: dat.name} as ApplicationAttachment} />
                ))}
                </div>
              </DetailItem>
              }

              { details?.note  && 
              <DetailItem labelText="Comments/Reason">{`${details.note}`}</DetailItem>
              }  
              { (isPopulatedArray(details?.noteAttachments as Array<IWebFileAttachment>)) &&
              <DetailItem labelText="Note Attachment/s">
                <div>
                { details.noteAttachments.map( (dat: IWebFileAttachment) => (
                <ApplicationFileDownload key={`note${dat.id}`} file={{id: dat.id, fileName: dat.name} as ApplicationAttachment} />
                ))}
                </div>
              </DetailItem>
              }
            </div>
          </div>
        </div>
      </div>
    </div>
  )
};